import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { APP_STORE_URL, PLAY_STORE_URL } from '../layout/AppStoreButtons';
import { getMarketplaceApiBaseUrl } from '../../api/resolveApiBaseUrl';
import { logger } from '../../utils/logger';

const IOS_UA = /iPhone|iPad|iPod/i;
const ANDROID_UA = /Android/i;

const recordDeferredDeepLink = async (path, platform) => {
  try {
    const base = process.env.REACT_APP_ITEMS_API_URL
      || getMarketplaceApiBaseUrl()
      || 'http://localhost:8080/api';
    await fetch(`${base.replace(/\/$/, '')}/public/deferred-links/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'omit',
      body: JSON.stringify({ path, platform }),
    });
  } catch (error) {
    logger.debug('Deferred deep link record skipped', error);
  }
};

const toAppSchemeUrl = (path) => {
  const clean = String(path || '').trim();
  if (!clean.startsWith('/')) return null;
  return `flea-app://${clean.replace(/^\//, '')}`;
};

const Banner = styled.aside`
  margin: 1.25rem 0 1.75rem;
  padding: 1rem 1.1rem;
  border-radius: 1rem;
  background: #1b4332;
  color: #f6f1e8;
`;

const Title = styled.p`
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  font-weight: 700;
`;

const Hint = styled.p`
  margin: 0 0 0.9rem;
  font-size: 0.9rem;
  line-height: 1.45;
  opacity: 0.9;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const ActionButton = styled.button`
  appearance: none;
  border: 0;
  border-radius: 999px;
  min-height: 42px;
  padding: 0.55rem 1rem;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  background: ${(p) => (p.$secondary ? 'transparent' : '#f6f1e8')};
  color: ${(p) => (p.$secondary ? '#f6f1e8' : '#1b4332')};
  border: ${(p) => (p.$secondary ? '1px solid rgba(246, 241, 232, 0.45)' : '1px solid transparent')};
`;

export default function OpenInFleaBanner({ path, titleKey = 'itemDetail.downloadFleaToShop' }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const isIos = IOS_UA.test(ua);
  const isAndroid = ANDROID_UA.test(ua);
  const isMobile = isIos || isAndroid;
  const destination = useMemo(() => {
    if (typeof window === 'undefined') return path;
    return path || `${window.location.pathname}${window.location.search || ''}`;
  }, [path]);

  if (!destination || !String(destination).startsWith('/')) return null;

  const rememberAndOpen = async (platform, href, { useAppScheme = false } = {}) => {
    if (busy) return;
    setBusy(true);
    try {
      await recordDeferredDeepLink(destination, platform);
      if (useAppScheme) {
        const appUrl = toAppSchemeUrl(destination);
        if (appUrl) {
          window.location.href = appUrl;
          window.setTimeout(() => {
            if (!document.hidden) {
              window.location.href = href;
            }
          }, 1400);
          return;
        }
      }
      window.location.href = href;
    } finally {
      setBusy(false);
    }
  };

  return (
    <Banner>
      <Title>{t(isMobile ? 'itemDetail.openInFlea' : titleKey)}</Title>
      <Hint>
        {isMobile
          ? t('itemDetail.openInFleaHint')
          : t('itemDetail.downloadFleaDesktopHint')}
      </Hint>
      <Actions>
        {isMobile && (
          <ActionButton
            type="button"
            disabled={busy}
            onClick={() => rememberAndOpen(isAndroid ? 'android' : 'ios', isAndroid ? PLAY_STORE_URL : APP_STORE_URL, { useAppScheme: true })}
          >
            {t('itemDetail.openInFlea')}
          </ActionButton>
        )}
        <ActionButton
          type="button"
          $secondary={isMobile}
          disabled={busy}
          onClick={() => rememberAndOpen(isAndroid ? 'android' : 'ios', isIos || !isAndroid ? APP_STORE_URL : PLAY_STORE_URL)}
        >
          {t('itemDetail.downloadFleaIos')}
        </ActionButton>
        <ActionButton
          type="button"
          $secondary
          disabled={busy}
          onClick={() => rememberAndOpen('android', PLAY_STORE_URL)}
        >
          {t('itemDetail.downloadFleaAndroid')}
        </ActionButton>
      </Actions>
    </Banner>
  );
}
